import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { pdf } from '@react-pdf/renderer'
import * as Sentry from '@sentry/react'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import { runVerification } from '../../../lib/verification/runVerification'
import { buildCIVerificationDataFromUpload } from '../../../lib/verification/ciFromUpload'
import { saveOverride, toOverrideCheckType } from '../../../lib/verification/overrides'
import type { CheckId, CheckOverride, OverrideStatus, VerificationResult } from '../../../lib/verification/types'
import {
  getAutoJobsDriveToken,
  clearAutoJobsDriveToken,
  initiateAutoJobsOAuth,
  listFolderFiles,
  downloadFileAsBase64,
  extractFolderIdFromUrl,
} from '../../../lib/api/google-drive-autojobs'
import ResultCard from './ResultCard'
import OverrideDialog from './OverrideDialog'
import VerificationReportPDF from './VerificationReportPDF'
import { downloadVerificationExcel, verificationWorkbookToBlob } from './exportVerificationExcel'
import type { BatchRowResult } from './exportVerificationExcel'

const MIN_ROWS = 1
const MAX_ROWS = 20
const DEFAULT_ROWS = 3
const CONCURRENCY = 3
const CI_FILE_ACCEPT = '.pdf,.xlsx,.xls'
const CI_FILE_RE = /\.(pdf|xlsx|xls)$/i
// Z62: auto-detect a job ref from a filename like "KI26033-01.pdf" or "KE12345.xlsx".
const JOB_REF_RE = /K[IE]\d{5}(-\d+)?/i
// Z62: lets the Verify page reuse the AutoJobs Drive OAuth round-trip (fixed, already-
// registered redirect_uri) without duplicating the auth flow -- see AutoJobsPage.tsx.
const DRIVE_EXTERNAL_RETURN_KEY = 'drive_oauth_external_return'
const DRIVE_PENDING_IMPORT_KEY = 'verify_drive_pending'

type RowStatus = 'empty' | 'ready' | 'parsing' | 'done' | 'error'
type RowSource = 'manual' | 'bulk' | 'drive'

interface VerifyRow {
  localId: string
  source: RowSource
  checked: boolean
  fileName: string
  file: File | null
  driveFileId?: string
  jobRef: string
  status: RowStatus
  error?: string
  archiveWarning?: string
  result?: VerificationResult
  runId?: string
  overrides: CheckOverride[]
  expanded: boolean
}

function detectJobRef(fileName: string): string {
  const m = fileName.match(JOB_REF_RE)
  return m ? m[0].toUpperCase() : ''
}

function makeManualRow(): VerifyRow {
  return { localId: crypto.randomUUID(), source: 'manual', checked: true, fileName: '', file: null, jobRef: '', status: 'empty', overrides: [], expanded: false }
}

function makeFileRow(source: RowSource, file: File): VerifyRow {
  const jobRef = detectJobRef(file.name)
  return { localId: crypto.randomUUID(), source, checked: true, fileName: file.name, file, jobRef, status: jobRef ? 'ready' : 'empty', overrides: [], expanded: false }
}

function makeDriveRow(f: { id: string; name: string }): VerifyRow {
  const jobRef = detectJobRef(f.name)
  return { localId: crypto.randomUUID(), source: 'drive', checked: true, fileName: f.name, file: null, driveFileId: f.id, jobRef, status: jobRef ? 'ready' : 'empty', overrides: [], expanded: false }
}

// Whether a row has enough data to verify (file/Drive ref + job ref), independent of the
// checkbox -- used for the row's own status badge, which shouldn't flip to "empty" just
// because staff unchecked it to exclude it from this run.
function rowHasData(row: VerifyRow): boolean {
  const hasSource = row.source === 'drive' ? !!row.driveFileId : !!row.file
  return row.jobRef.trim().length > 0 && hasSource
}

// Whether a row should actually be processed by "Run Verification": checked AND has data.
function rowIsValid(row: VerifyRow): boolean {
  return row.checked && rowHasData(row)
}

interface PendingOverride {
  rowId: string
  checkId: CheckId
  checkTitle: string
  status: OverrideStatus
}

function rowStatusLabel(row: VerifyRow): string {
  switch (row.status) {
    case 'empty': return ''
    case 'ready': return 'Ready'
    case 'parsing': return 'Parsing...'
    case 'error': return `Error: ${row.error}`
    case 'done': {
      if (!row.result) return 'Done'
      if (row.result.noInvoicesFound) return 'No CI data'
      if (row.result.failCount === 0) return row.result.naCount > 0 ? `Pass (${row.result.naCount} N/A)` : 'Pass'
      return `${row.result.failCount} Failed`
    }
  }
}

function rowStatusClass(row: VerifyRow): string {
  if (row.status === 'error') return 'text-red-600 dark:text-red-400'
  if (row.status === 'done' && row.result) {
    return row.result.noInvoicesFound || row.result.failCount > 0
      ? 'text-red-600 dark:text-red-400'
      : 'text-green-600 dark:text-green-400'
  }
  if (row.status === 'parsing') return 'text-blue-600 dark:text-blue-400'
  return 'text-gray-500 dark:text-gray-400'
}

function base64ToFile(base64: string, fileName: string): File {
  const lower = fileName.toLowerCase()
  const mime = lower.endsWith('.pdf')
    ? 'application/pdf'
    : lower.endsWith('.xls')
      ? 'application/vnd.ms-excel'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  const byteChars = atob(base64)
  const bytes = new Uint8Array(byteChars.length)
  for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i)
  return new File([bytes], fileName, { type: mime })
}

export default function VerifyPage() {
  const navigate = useNavigate()
  const { userEmail } = useAuth()
  const [rows, setRows] = useState<VerifyRow[]>(() => Array.from({ length: DEFAULT_ROWS }, makeManualRow))
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [pendingOverride, setPendingOverride] = useState<PendingOverride | null>(null)
  const [savingOverride, setSavingOverride] = useState(false)
  const [overrideError, setOverrideError] = useState<string | null>(null)
  const [downloadingExcel, setDownloadingExcel] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [downloadWarning, setDownloadWarning] = useState<string | null>(null)

  const bulkInputRef = useRef<HTMLInputElement>(null)

  // Method 3: Google Drive import
  const [driveToken, setDriveToken] = useState<string | null>(null)
  const [showDriveInput, setShowDriveInput] = useState(false)
  const [driveFolderUrl, setDriveFolderUrl] = useState('')
  const [driveImporting, setDriveImporting] = useState(false)
  const [driveError, setDriveError] = useState<string | null>(null)

  // Restore a cached Drive token, and resume a folder import interrupted by the OAuth
  // round-trip through AutoJobsPage (see google-drive-autojobs.ts / AutoJobsPage.tsx).
  useEffect(() => {
    const saved = getAutoJobsDriveToken()
    if (saved) setDriveToken(saved)

    const pending = sessionStorage.getItem(DRIVE_PENDING_IMPORT_KEY)
    if (!pending) return
    sessionStorage.removeItem(DRIVE_PENDING_IMPORT_KEY)
    try {
      const { folderUrl } = JSON.parse(pending) as { folderUrl: string }
      if (folderUrl && saved) {
        setDriveFolderUrl(folderUrl)
        setShowDriveInput(true)
        importFromDrive(folderUrl, saved)
      }
    } catch {
      // malformed pending-state -- nothing to resume
    }
  }, [])

  const canAddRow = rows.length < MAX_ROWS
  const canRemoveRow = rows.length > MIN_ROWS
  const canRun = rows.some(rowIsValid) && !running

  function addRow() {
    if (canAddRow) setRows(prev => [...prev, makeManualRow()])
  }

  function removeLastRow() {
    if (canRemoveRow) setRows(prev => prev.slice(0, -1))
  }

  function updateRow(localId: string, patch: Partial<VerifyRow>) {
    setRows(prev => prev.map(r => (r.localId === localId ? { ...r, ...patch } : r)))
  }

  function toggleChecked(localId: string) {
    setRows(prev => prev.map(r => (r.localId === localId ? { ...r, checked: !r.checked } : r)))
  }

  function setJobRef(localId: string, jobRef: string) {
    setRows(prev => prev.map(r => (r.localId === localId ? { ...r, jobRef, status: rowHasData({ ...r, jobRef }) ? 'ready' : 'empty' } : r)))
  }

  function setFile(localId: string, file: File | null) {
    setRows(prev => prev.map(r => {
      if (r.localId !== localId) return r
      const jobRef = r.jobRef.trim() ? r.jobRef : (file ? detectJobRef(file.name) : r.jobRef)
      const next = { ...r, file, fileName: file?.name ?? '', jobRef }
      return { ...next, status: rowHasData(next) ? 'ready' : 'empty' }
    }))
  }

  function toggleExpanded(localId: string) {
    setRows(prev => prev.map(r => (r.localId === localId ? { ...r, expanded: !r.expanded } : r)))
  }

  // Method 2: bulk upload -- each locally-selected file becomes its own row.
  function handleBulkFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    e.target.value = '' // allow re-selecting the same file(s) again later
    if (files.length === 0) return
    setRows(prev => [...prev, ...files.map(f => makeFileRow('bulk', f))])
  }

  // Method 3: Google Drive import
  async function importFromDrive(folderUrl: string, token: string) {
    const folderId = extractFolderIdFromUrl(folderUrl)
    if (!folderId) { setDriveError("Couldn't find a folder ID in that URL"); return }
    try {
      const files = await listFolderFiles(folderId, token)
      const ciFiles = files.filter(f => CI_FILE_RE.test(f.name))
      if (ciFiles.length === 0) { setDriveError('No .pdf/.xls/.xlsx files found in that folder'); return }
      setRows(prev => [...prev, ...ciFiles.map(makeDriveRow)])
      setShowDriveInput(false)
      setDriveFolderUrl('')
    } catch (e: unknown) {
      if (e instanceof Error && e.message === 'UNAUTHORIZED') {
        clearAutoJobsDriveToken()
        setDriveToken(null)
        sessionStorage.setItem(DRIVE_PENDING_IMPORT_KEY, JSON.stringify({ folderUrl }))
        sessionStorage.setItem(DRIVE_EXTERNAL_RETURN_KEY, '/customs/verify')
        initiateAutoJobsOAuth()
        return
      }
      setDriveError(e instanceof Error ? e.message : 'Failed to list Drive folder')
    }
  }

  async function handleDriveImportClick() {
    const url = driveFolderUrl.trim()
    if (!url) return
    setDriveError(null)
    setDriveImporting(true)
    try {
      const token = driveToken ?? getAutoJobsDriveToken()
      if (!token) {
        sessionStorage.setItem(DRIVE_PENDING_IMPORT_KEY, JSON.stringify({ folderUrl: url }))
        sessionStorage.setItem(DRIVE_EXTERNAL_RETURN_KEY, '/customs/verify')
        initiateAutoJobsOAuth()
        return
      }
      await importFromDrive(url, token)
    } finally {
      setDriveImporting(false)
    }
  }

  async function processRow(row: VerifyRow): Promise<void> {
    const jobRef = row.jobRef.trim().toUpperCase()
    updateRow(row.localId, { status: 'parsing', error: undefined })

    try {
      let file: File
      if (row.source === 'drive') {
        if (!row.driveFileId) throw new Error('Missing Drive file reference')
        const token = driveToken ?? getAutoJobsDriveToken()
        if (!token) throw new Error('Google Drive session expired -- re-import this folder from Drive')
        const base64 = await downloadFileAsBase64(row.driveFileId, token)
        file = base64ToFile(base64, row.fileName)
      } else {
        if (!row.file) throw new Error('No file selected')
        file = row.file
      }

      const storagePath = `verification-reports/${jobRef}/${Date.now()}_${file.name}`
      const { error: uploadErr } = await supabase.storage
        .from('verification-reports')
        .upload(storagePath, file, { contentType: file.type || 'application/octet-stream', upsert: true, cacheControl: '0' })

      let archiveWarning: string | undefined
      if (uploadErr) {
        Sentry.captureException(uploadErr, { tags: { feature: 'verify_ci_archive_upload' }, extra: { jobRef, storagePath } })
        archiveWarning = `Original file failed to archive (Sentry logged): ${uploadErr.message}`
      }

      const { ciData, meta } = await buildCIVerificationDataFromUpload(file, jobRef)
      if (!uploadErr) meta.storageKey = storagePath

      const result = await runVerification(jobRef, ciData, meta, userEmail || 'system')

      const { data: inserted, error: insertError } = await supabase
        .from('verification_runs')
        .insert({
          job_ref: result.jobRef,
          cis_included: result.cisIncluded,
          verified_at: result.verifiedAt,
          verified_by: result.verifiedBy,
          pass_count: result.passCount,
          fail_count: result.failCount,
          details_json: result,
        })
        .select('id')
        .single()

      if (insertError) {
        Sentry.captureException(insertError, { tags: { feature: 'verify_run_insert' }, extra: { jobRef } })
        console.error('Failed to save verification run:', insertError)
      }

      updateRow(row.localId, {
        status: 'done',
        result,
        runId: inserted?.id,
        overrides: [],
        expanded: true,
        archiveWarning,
      })
    } catch (e: unknown) {
      updateRow(row.localId, { status: 'error', error: e instanceof Error ? e.message : 'Verification failed' })
    }
  }

  async function handleRun() {
    const queue = rows.filter(rowIsValid)
    setRunning(true)
    setProgress({ done: 0, total: queue.length })
    async function worker() {
      while (queue.length > 0) {
        const next = queue.shift()
        if (!next) return
        await processRow(next)
        setProgress(p => (p ? { done: p.done + 1, total: p.total } : p))
      }
    }
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker))
    setRunning(false)
    setProgress(null)
  }

  function openOverrideDialog(rowId: string, checkId: CheckId, checkTitle: string, status: OverrideStatus) {
    setOverrideError(null)
    setPendingOverride({ rowId, checkId, checkTitle, status })
  }

  async function handleSaveOverride(notes: string) {
    if (!pendingOverride) return
    const row = rows.find(r => r.localId === pendingOverride.rowId)
    if (!row?.runId) return
    setSavingOverride(true)
    setOverrideError(null)
    try {
      const saved = await saveOverride(row.runId, toOverrideCheckType(pendingOverride.checkId), pendingOverride.status, notes, userEmail || 'system')
      updateRow(row.localId, { overrides: [...row.overrides.filter(o => o.checkType !== saved.checkType), saved] })
      setPendingOverride(null)
    } catch (e: unknown) {
      setOverrideError(e instanceof Error ? e.message : 'Failed to save override')
    } finally {
      setSavingOverride(false)
    }
  }

  const completedBatchRows: BatchRowResult[] = rows
    .filter((r): r is VerifyRow & { result: VerificationResult } => !!r.result)
    .map(r => ({ result: r.result, overrides: r.overrides }))

  async function handleDownloadExcel() {
    if (completedBatchRows.length === 0) return
    setDownloadingExcel(true)
    setDownloadWarning(null)
    try {
      downloadVerificationExcel(completedBatchRows)
      const blob = verificationWorkbookToBlob(completedBatchRows)
      const path = `verification-reports/_batch/batch_verification_${Date.now()}.xlsx`
      const { error: uploadErr } = await supabase.storage.from('verification-reports').upload(path, blob, {
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        upsert: true,
        cacheControl: '0',
      })
      if (uploadErr) {
        Sentry.captureException(uploadErr, { tags: { feature: 'verify_batch_excel_archive' }, extra: { path } })
        setDownloadWarning(`Excel downloaded, but archiving failed (your local copy is still valid): ${uploadErr.message}`)
        return
      }
      const runIds = rows.map(r => r.runId).filter((id): id is string => !!id)
      if (runIds.length > 0) {
        const { error: linkErr } = await supabase.from('verification_runs').update({ report_excel_key: path }).in('id', runIds)
        if (linkErr) Sentry.captureException(linkErr, { tags: { feature: 'verify_batch_excel_link' }, extra: { path } })
      }
    } finally {
      setDownloadingExcel(false)
    }
  }

  async function handleDownloadPdf() {
    if (completedBatchRows.length === 0) return
    setDownloadingPdf(true)
    setDownloadWarning(null)
    try {
      const blob = await pdf(<VerificationReportPDF rows={completedBatchRows} />).toBlob()
      const fileName = `batch_verification_${Date.now()}.pdf`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      URL.revokeObjectURL(url)

      const path = `verification-reports/_batch/${fileName}`
      const { error: uploadErr } = await supabase.storage.from('verification-reports').upload(path, blob, { contentType: 'application/pdf', upsert: true, cacheControl: '0' })
      if (uploadErr) {
        Sentry.captureException(uploadErr, { tags: { feature: 'verify_batch_pdf_archive' }, extra: { path } })
        setDownloadWarning(`PDF downloaded, but archiving failed (your local copy is still valid): ${uploadErr.message}`)
        return
      }
      const runIds = rows.map(r => r.runId).filter((id): id is string => !!id)
      if (runIds.length > 0) {
        const { error: linkErr } = await supabase.from('verification_runs').update({ report_pdf_key: path }).in('id', runIds)
        if (linkErr) Sentry.captureException(linkErr, { tags: { feature: 'verify_batch_pdf_link' }, extra: { path } })
      }
    } finally {
      setDownloadingPdf(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
          <button onClick={() => navigate('/customs')} className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
            Customs
          </button>
          <span>/</span>
          <span className="text-gray-900 dark:text-white">Three-Way Verification</span>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Three-Way Verification</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Add rows manually, bulk-upload files from disk, or import a Google Drive folder -- mix and match freely.
            Nothing is looked up from the Commercial Invoice module automatically; each row's file is the only source
            of CI data checked for that job.
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <th className="pb-2 pr-3 w-8"><span className="sr-only">Include</span></th>
                  <th className="pb-2 pr-3 w-10">#</th>
                  <th className="pb-2 pr-3">Filename</th>
                  <th className="pb-2 pr-3">Job Reference</th>
                  <th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((row, idx) => (
                  <tr key={row.localId} className={!row.checked ? 'opacity-50' : ''}>
                    <td className="py-2 pr-3">
                      <input
                        type="checkbox"
                        checked={row.checked}
                        disabled={running}
                        onChange={() => toggleChecked(row.localId)}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                    </td>
                    <td className="py-2 pr-3 text-gray-500 dark:text-gray-400">{idx + 1}</td>
                    <td className="py-2 pr-3">
                      {row.source === 'manual' ? (
                        <input
                          type="file"
                          accept={CI_FILE_ACCEPT}
                          disabled={running}
                          onChange={e => setFile(row.localId, e.target.files?.[0] ?? null)}
                          className="text-xs text-gray-600 dark:text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900/30 dark:file:text-blue-400 hover:file:bg-blue-100 disabled:opacity-60"
                        />
                      ) : (
                        <span className="text-xs text-gray-700 dark:text-gray-300">
                          {row.fileName}
                          <span className="ml-1.5 text-[10px] uppercase text-gray-400">{row.source === 'drive' ? 'Drive' : 'Bulk'}</span>
                        </span>
                      )}
                      {row.source === 'manual' && !row.fileName && (
                        <span className="ml-1.5 text-xs text-gray-400">Manual entry</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        value={row.jobRef}
                        onChange={e => setJobRef(row.localId, e.target.value)}
                        placeholder="e.g. KI26033-01"
                        disabled={running}
                        className="w-40 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-60"
                      />
                    </td>
                    <td className={`py-2 text-xs font-medium ${rowStatusClass(row)}`}>{rowStatusLabel(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-4">
            <button
              onClick={addRow}
              disabled={!canAddRow || running}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              + Add Row
            </button>
            <button
              onClick={removeLastRow}
              disabled={!canRemoveRow || running}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              Remove Last Row
            </button>
            <button
              onClick={() => bulkInputRef.current?.click()}
              disabled={running}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              Bulk Upload Files
            </button>
            <input
              ref={bulkInputRef}
              type="file"
              multiple
              accept={CI_FILE_ACCEPT}
              onChange={handleBulkFilesSelected}
              className="hidden"
            />
            <button
              onClick={() => setShowDriveInput(v => !v)}
              disabled={running}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              Import from Google Drive
            </button>
            <div className="flex-1" />
            <button
              onClick={handleRun}
              disabled={!canRun}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors"
            >
              {running && progress ? `Verifying ${progress.done} of ${progress.total}...` : running ? 'Verifying...' : 'Run Verification'}
            </button>
          </div>

          {showDriveInput && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Google Drive folder URL</label>
              <div className="flex flex-wrap gap-2">
                <input
                  value={driveFolderUrl}
                  onChange={e => setDriveFolderUrl(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/..."
                  disabled={driveImporting}
                  className="flex-1 min-w-[280px] px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-60"
                />
                <button
                  onClick={handleDriveImportClick}
                  disabled={driveImporting || !driveFolderUrl.trim()}
                  className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  {driveImporting ? 'Importing...' : 'Import'}
                </button>
                <button
                  onClick={() => { setShowDriveInput(false); setDriveError(null) }}
                  disabled={driveImporting}
                  className="px-4 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
              {driveError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{driveError}</p>}
              <p className="mt-2 text-xs text-gray-400">
                Lists .pdf/.xls/.xlsx files in the folder. If your Drive session isn't authorized yet, this will
                redirect through Google and bring you back here automatically.
              </p>
            </div>
          )}
        </div>

        {completedBatchRows.length > 0 && (
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {completedBatchRows.length} job{completedBatchRows.length !== 1 ? 's' : ''} verified
            </h2>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDownloadExcel}
                disabled={downloadingExcel}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                {downloadingExcel ? 'Preparing...' : 'Summary Report — Download Batch Excel'}
              </button>
              <button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                {downloadingPdf ? 'Preparing...' : 'Download Batch PDF'}
              </button>
            </div>
          </div>
        )}
        {downloadWarning && (
          <p className="mb-4 text-xs text-amber-700 dark:text-amber-400">{downloadWarning}</p>
        )}

        <div className="space-y-4">
          {rows.filter(r => r.result).map(row => (
            <div key={row.localId} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <button
                onClick={() => toggleExpanded(row.localId)}
                className="w-full flex items-center justify-between p-4 text-left"
              >
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">{row.result!.jobRef}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{row.fileName}</span>
                  {row.result!.consol && (
                    <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                      consol: parent {row.result!.consol.parentJobRef} + {row.result!.consol.childJobRefs.length} child(ren)
                    </span>
                  )}
                </div>
                <span className={`text-sm font-semibold ${rowStatusClass(row)}`}>{rowStatusLabel(row)} {row.expanded ? '▲' : '▼'}</span>
              </button>
              {row.archiveWarning && (
                <p className="px-4 pb-2 -mt-2 text-xs text-amber-700 dark:text-amber-400">{row.archiveWarning}</p>
              )}

              {row.expanded && (
                <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {row.result!.checks.map(check => (
                    <ResultCard
                      key={check.id}
                      check={check}
                      override={row.overrides.find(o => o.checkType === toOverrideCheckType(check.id))}
                      onOverride={row.runId ? status => openOverrideDialog(row.localId, check.id, check.title, status) : undefined}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {pendingOverride && (
          <OverrideDialog
            checkTitle={pendingOverride.checkTitle}
            pendingStatus={pendingOverride.status}
            initialNotes={
              rows
                .find(r => r.localId === pendingOverride.rowId)
                ?.overrides.find(o => o.checkType === toOverrideCheckType(pendingOverride.checkId) && o.overrideStatus === pendingOverride.status)?.notes
            }
            saving={savingOverride}
            error={overrideError}
            onCancel={() => setPendingOverride(null)}
            onSave={handleSaveOverride}
          />
        )}
      </div>
    </div>
  )
}
